// antd v5 auto-forces tableLayout to "fixed" whenever any column sets `ellipsis: true`, and
// its own base CSS stretches the inner <table> to 100% of its container (via an inline
// `min-width: 100%` alongside `width: max-content`). Per the CSS2.1 fixed-table-layout spec,
// when a table's resolved width exceeds the sum of its column widths, browsers distribute the
// surplus proportionally across every column that has a set width — not just the widest one —
// which is what made columns like "Note" balloon and pushed "Actions" out of view across many
// list tables after the v4->v5 upgrade (v4 never forced tableLayout: fixed this way).
//
// Every affected table's own <colgroup> already has the correct per-column widths (that's how
// the header renders correctly) — this reads that colgroup and pins the <table>'s own width to
// the sum, which removes the surplus so nothing gets redistributed. Runs generically against
// any table in the app rather than requiring a per-page CSS override.
const fixTable = table => {
  if (table.dataset.widthFixed === 'done') return;
  const colgroup = table.querySelector(':scope > colgroup');
  if (!colgroup) return;
  const cols = Array.from(colgroup.children);
  if (cols.length === 0) return;
  let sum = 0;
  for (const col of cols) {
    const w = parseFloat(col.style.width);
    if (!w) return; // a column with no explicit width means this table is meant to flex — leave it alone
    sum += w;
  }
  // Chromium computes a `width: max-content` table's intrinsic size from each cell's true,
  // unclipped content width — it ignores `text-overflow: ellipsis` entirely when doing this,
  // so any column with genuinely long text (e.g. Support Table's Description) blows out to
  // that content's full width regardless of its declared column width. Replacing max-content
  // with a concrete pixel width avoids that intrinsic-sizing pass altogether, so ellipsis
  // then clips correctly against the real, final column width.
  //
  // Two different tables want two different concrete widths here:
  // - Most tables (default): stretched to fill the container, so declared widths become
  //   proportional minimums and leftover space is actually used instead of left blank —
  //   this used to be opt-in via `.fill-table-width`, but nearly every list table in the app
  //   wanted it (only one table ever opted in on purpose) while every other narrow table was
  //   silently leaving a blank unbordered gap after its last column, so it's the default now.
  // - Tables marked `.pinned-table-width` are pinned to the exact sum of their columns
  //   instead, for the rare case where stretching would look wrong.
  const fillContainer = !table.closest('.pinned-table-width');
  const containerWidth = fillContainer ? table.closest('.ant-table-wrapper')?.getBoundingClientRect().width : null;
  const finalWidth = containerWidth && containerWidth > sum ? containerWidth : sum;
  // antd also sets inline `min-width: 100%` alongside `width` (for the horizontal-scroll
  // case) — the used width is max(width, min-width), so min-width silently wins unless it's
  // cleared too.
  table.style.width = `${finalWidth}px`;
  table.style.minWidth = '0';
  table.dataset.widthFixed = 'done';
};

export const fixTableWidths = (root = document) => {
  root.querySelectorAll('.ant-table-content > table, .ant-table-container > table').forEach(fixTable);
};

let observer;
export const startTableWidthFixObserver = () => {
  if (observer) return;
  fixTableWidths();
  observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.('table')) fixTable(node);
        node.querySelectorAll?.('.ant-table-content > table, .ant-table-container > table').forEach(fixTable);
      }
      // Re-run on attribute changes too: sorting/filtering can resize colgroup <col> widths
      // in place (same <table> node) without an addedNodes event.
      if (mutation.type === 'attributes' && mutation.target.tagName === 'COL') {
        const table = mutation.target.closest('table');
        if (table) {
          table.dataset.widthFixed = '';
          fixTable(table);
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
};
